import { useState, useContext, useEffect } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Package, Plus, Search, Filter, RefreshCw, Box, Layers, TrendingUp, TrendingDown, ArrowUp, ArrowDown, Edit2, X, Save } from 'lucide-react';

function FinishedGoodsStock() {
  const { API } = useContext(AppContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [finishedGoods, setFinishedGoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Adjustment modal state
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [adjustmentValue, setAdjustmentValue] = useState('');
  const [saving, setSaving] = useState(false);

  const categories = ['all', 'Cubes', 'Notchbar', 'Shots', 'Ingots'];

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/dispatch/finished-goods`);
      setFinishedGoods(response.data || []);
    } catch (err) {
      console.error('Error fetching finished goods:', err);
      setError('Failed to load finished goods data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [API]);

  // Open adjustment modal
  const openAdjustModal = (item) => {
    setAdjustingItem(item);
    setAdjustmentValue(item.adjustment?.toString() || '0');
    setShowAdjustModal(true);
  };

  // Save adjustment
  const handleSaveAdjustment = async () => {
    if (!adjustingItem) return;
    
    setSaving(true);
    try {
      const adjValue = parseFloat(adjustmentValue) || 0;
      await axios.put(`${API}/dispatch/finished-goods/${adjustingItem.category}/adjust?adjustment_mt=${adjValue}`);
      setShowAdjustModal(false);
      setAdjustingItem(null);
      fetchData(); // Refresh data
    } catch (err) {
      console.error('Error saving adjustment:', err);
      alert('Failed to save adjustment');
    } finally {
      setSaving(false);
    }
  };

  const filteredGoods = finishedGoods.filter(item => {
    const matchesSearch = (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Cubes': return 'bg-blue-100 text-blue-700';
      case 'Notchbar': return 'bg-purple-100 text-purple-700';
      case 'Shots': return 'bg-orange-100 text-orange-700';
      case 'Ingots': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStockStatus = (stock, minStock) => {
    if (!minStock || minStock === 0) return { color: 'text-gray-600', bg: 'bg-gray-100', label: 'No Min Set' };
    const ratio = stock / minStock;
    if (ratio >= 2) return { color: 'text-green-600', bg: 'bg-green-100', label: 'Healthy' };
    if (ratio >= 1) return { color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Normal' };
    return { color: 'text-red-600', bg: 'bg-red-100', label: 'Low Stock' };
  };

  // Summary stats
  const totalStock = finishedGoods.reduce((sum, item) => sum + (item.stock || 0), 0);
  const lowStockItems = finishedGoods.filter(item => item.minStock && item.stock < item.minStock).length;
  const categoryWise = {
    Cubes: finishedGoods.filter(i => i.category === 'Cubes').reduce((s, i) => s + (i.stock || 0), 0),
    Notchbar: finishedGoods.filter(i => i.category === 'Notchbar').reduce((s, i) => s + (i.stock || 0), 0),
    Shots: finishedGoods.filter(i => i.category === 'Shots').reduce((s, i) => s + (i.stock || 0), 0),
    Ingots: finishedGoods.filter(i => i.category === 'Ingots').reduce((s, i) => s + (i.stock || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#1a237e]" size={32} />
        <span className="ml-2 text-gray-600">Loading finished goods...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finished-goods-stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="text-[#1a237e]" size={28} />
            Finished Goods Stock
          </h1>
          <p className="text-gray-600 mt-1">Aluminium products ready for dispatch</p>
        </div>
        <button 
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-[#1a237e] text-white rounded-lg hover:bg-[#283593] transition"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-[#1a237e] to-[#283593] rounded-xl p-4 text-white">
          <div className="flex items-center gap-2">
            <Box className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Total Stock</p>
          </div>
          <p className="text-2xl font-bold mt-1">{(totalStock / 1000).toFixed(1)} T</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Cubes</p>
          <p className="text-2xl font-bold mt-1">{(categoryWise.Cubes / 1000).toFixed(1)} T</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Notchbar</p>
          <p className="text-2xl font-bold mt-1">{(categoryWise.Notchbar / 1000).toFixed(1)} T</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Shots</p>
          <p className="text-2xl font-bold mt-1">{(categoryWise.Shots / 1000).toFixed(1)} T</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-sm opacity-80">Ingots</p>
          <p className="text-2xl font-bold mt-1">{(categoryWise.Ingots / 1000).toFixed(1)} T</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
            data-testid="search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-500" />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-transparent"
            data-testid="category-filter"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Item</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Category</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-green-700">
                  <div className="flex items-center justify-end gap-1">
                    <ArrowUp size={14} />
                    Produced (MT)
                  </div>
                </th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-red-700">
                  <div className="flex items-center justify-end gap-1">
                    <ArrowDown size={14} />
                    Dispatched (MT)
                  </div>
                </th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-amber-700">
                  <div className="flex items-center justify-end gap-1">
                    <Edit2 size={14} />
                    Adjustment (MT)
                  </div>
                </th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-blue-700">Available Stock</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Status</th>
                <th className="text-center px-4 py-3 text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredGoods.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    {finishedGoods.length === 0 
                      ? 'No finished goods data. Production entries will automatically add to stock.'
                      : 'No items match your search criteria.'
                    }
                  </td>
                </tr>
              ) : (
                filteredGoods.map((item) => {
                  const status = getStockStatus(item.stock || 0, item.minStock || 0);
                  const availableMT = item.stockMT || (item.stock || 0) / 1000;
                  return (
                    <tr key={item.id} className="hover:bg-gray-50 transition" data-testid={`fg-row-${item.id}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(item.category)}`}>
                          {item.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-green-600">
                          +{(item.produced || 0).toFixed(2)} MT
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-red-600">
                          -{(item.dispatched || 0).toFixed(2)} MT
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${(item.adjustment || 0) >= 0 ? 'text-amber-600' : 'text-orange-600'}`}>
                          {(item.adjustment || 0) >= 0 ? '+' : ''}{(item.adjustment || 0).toFixed(2)} MT
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xl font-bold ${availableMT > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                          {availableMT.toFixed(2)} MT
                        </span>
                        <p className="text-xs text-gray-500">{((item.stock || 0)).toLocaleString()} kg</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => openAdjustModal(item)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100 transition"
                          title="Adjust Stock"
                        >
                          <Edit2 size={14} />
                          Adjust
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Adjustment Modal */}
      {showAdjustModal && adjustingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Adjust Stock</h2>
              <button onClick={() => { setShowAdjustModal(false); setAdjustingItem(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {/* Item Info */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{adjustingItem.name}</p>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(adjustingItem.category)}`}>
                    {adjustingItem.category}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Current Stock</p>
                  <p className="text-xl font-bold text-blue-600">{adjustingItem.stockMT?.toFixed(2) || '0.00'} MT</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-gray-500">Produced</p>
                  <p className="font-medium text-green-600">+{adjustingItem.produced?.toFixed(2) || '0.00'} MT</p>
                </div>
                <div>
                  <p className="text-gray-500">Dispatched</p>
                  <p className="font-medium text-red-600">-{adjustingItem.dispatched?.toFixed(2) || '0.00'} MT</p>
                </div>
                <div>
                  <p className="text-gray-500">Current Adj.</p>
                  <p className="font-medium text-amber-600">{adjustingItem.adjustment?.toFixed(2) || '0.00'} MT</p>
                </div>
              </div>
            </div>

            {/* Adjustment Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Set Adjustment Value (MT)
              </label>
              <input
                type="number"
                step="0.01"
                value={adjustmentValue}
                onChange={(e) => setAdjustmentValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-lg font-semibold text-center focus:border-amber-500 focus:ring-2 focus:ring-amber-100 outline-none"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-2">
                <strong>Formula:</strong> Available = Production ({adjustingItem.produced?.toFixed(2) || 0}) - Dispatch ({adjustingItem.dispatched?.toFixed(2) || 0}) + Adjustment ({adjustmentValue || 0})
              </p>
              <p className="text-xs text-amber-600 mt-1">
                <strong>Result:</strong> {Math.max(0, (adjustingItem.produced || 0) - (adjustingItem.dispatched || 0) + (parseFloat(adjustmentValue) || 0)).toFixed(2)} MT
              </p>
            </div>

            {/* Quick Buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setAdjustmentValue((adjustingItem.dispatched || 0).toFixed(2))}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200"
              >
                Set to match Dispatched ({adjustingItem.dispatched?.toFixed(2) || 0} MT)
              </button>
              <button
                onClick={() => setAdjustmentValue('0')}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200"
              >
                Reset to 0
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setShowAdjustModal(false); setAdjustingItem(null); }}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAdjustment}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <><RefreshCw className="animate-spin" size={16} />Saving...</>
                ) : (
                  <><Save size={16} />Save Adjustment</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Layers className="text-blue-600 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-blue-900">Automatic Stock Calculation</p>
            <p className="text-sm text-blue-700 mt-1">
              <strong>Available Stock = Production - Dispatch + Adjustment</strong><br/>
              • <span className="text-green-600 font-medium">Production</span>: Auto-added from Production Entries<br/>
              • <span className="text-red-600 font-medium">Dispatch</span>: Auto-deducted from Dispatch Records<br/>
              • <span className="text-amber-600 font-medium">Adjustment</span>: Manual correction (click "Adjust" to set opening stock or correct discrepancies)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FinishedGoodsStock;
